import type { FunnelStep } from './installForm';

/**
 * What a funnel step can be pointed at, and the funnels worth starting from.
 *
 * The passenger app and the booking widgets are ours, so what they report is
 * known rather than guessed — and every entry here is something that ACTUALLY
 * reaches the collector today, read off the reporting code rather than off what
 * would be nice. A choice that never fires is worse than no choice: the funnel
 * card shows it as a step nobody ever reaches, which reads as a problem with
 * the business rather than with the list.
 *
 * - **The app** reports a screen as its route name, verbatim (GetX named
 *   routes with no parameters in them), plus two events. The booking form is
 *   ONE route, `/book-now`; from the release that added it the app also reports
 *   each of its five steps as `/book-now/<step>`. Copies of the app already on
 *   phones never will, so those five are offered only once THIS install has
 *   reported them (`onceSeen`) — until then the form is one step, and the
 *   step-by-step templates are not offered.
 * - **The booking widgets** report no pages and no steps of their own: they sit
 *   in a frame, so every pageview is the HOST page's path, and the loader
 *   relays exactly two events from the widget, `booking_confirmed` and
 *   `quote_requested`. The host pages are the tenant's own, so they come from
 *   what this install has been seen reporting instead.
 * - **Any website** also has the tag's own events (console/public/analytics.js):
 *   forms and contact links on the host pages — never inside the widget frame,
 *   which the tag cannot see into.
 *
 * Steps are matched as the funnel read matches them (controller/analyticsCont.js
 * `funnel`): an event by exact name, a page by the START of its path — so `/`
 * is "any page at all", and `/fleet` also counts `/fleet-details`. The notes
 * say so where it bites.
 */

export type MatchType = 'path' | 'event';

export interface StepChoice {
  type: MatchType;
  value: string;
  label: string;
  /** Shown beside the value: what the operator would not guess from the label. */
  note?: string;
  /** Offered only once this install has reported it — see the app's booking steps. */
  onceSeen?: boolean;
}

/** Which list an install gets: our app, a site the booking widgets report to, or any other site. */
export type InstallKind = 'app' | 'widgets' | 'web';

export const installKind = (platform: string, widgetLinked: boolean): InstallKind =>
  platform === 'app' ? 'app' : widgetLinked ? 'widgets' : 'web';

const ANY_SCREEN: StepChoice = { type: 'path', value: '/', label: 'Opened the app', note: 'any screen' };
const ANY_PAGE: StepChoice = { type: 'path', value: '/', label: 'Landed on the site', note: 'any page' };
const BOOKED: StepChoice = { type: 'event', value: 'booking_confirmed', label: 'Booked' };
const QUOTED: StepChoice = { type: 'event', value: 'quote_requested', label: 'Requested a quote' };

/**
 * The booking form's steps, as the app reports them
 * (lib/app/helpers/sphere_analytics_helper.dart `bookingStepScreens`). Payment
 * is the sheet the summary opens, and the page itself when a quote is accepted.
 */
const BOOKING_STEPS: StepChoice[] = [
  { type: 'path', value: '/book-now/trip', label: 'Reservation form: trip', onceSeen: true },
  { type: 'path', value: '/book-now/vehicle', label: 'Reservation form: vehicle', onceSeen: true },
  { type: 'path', value: '/book-now/details', label: 'Reservation form: details', onceSeen: true },
  { type: 'path', value: '/book-now/summary', label: 'Reservation form: summary', note: 'reservations only', onceSeen: true },
  { type: 'path', value: '/book-now/payment', label: 'Reservation form: payment', note: 'reservations only', onceSeen: true },
];

/** Whether this install's app reports the booking form step by step. */
export const reportsBookingSteps = (seenPaths: readonly string[]): boolean =>
  BOOKING_STEPS.some((choice) => seenPaths.includes(choice.value));

/** The customer app's routes (lib/app/routes/app_routes.dart), in roughly the order a booking meets them. */
const APP_CHOICES: StepChoice[] = [
  ANY_SCREEN,
  { type: 'path', value: '/bottom-bar', label: 'Home', note: 'its Bookings and Account tabs count as Home too' },
  { type: 'path', value: '/fleet', label: 'Fleet', note: 'also counts a vehicle’s own screen' },
  { type: 'path', value: '/fleet-details', label: 'A vehicle’s details' },
  { type: 'path', value: '/book-now', label: 'Reservation form', note: 'every step of it, trip to payment' },
  ...BOOKING_STEPS,
  QUOTED,
  BOOKED,
  {
    type: 'path',
    value: '/order-success',
    label: 'Confirmation screen',
    note: 'also opening a past reservation — Booked is the one that counts reservations',
  },
  { type: 'path', value: '/login', label: 'Sign-in screen', note: 'most sign-ins use a sheet, which is not a screen' },
  { type: 'path', value: '/register', label: 'Create an account' },
  { type: 'path', value: '/otp-verification', label: 'Verify the phone number' },
  { type: 'path', value: '/forgot-password', label: 'Forgot password' },
  { type: 'path', value: '/ongoing-rides', label: 'Ongoing rides' },
  { type: 'path', value: '/googlemap', label: 'Live driver tracking' },
  { type: 'path', value: '/booking', label: 'Bookings list', note: 'usually a tab on Home, which counts as Home' },
  { type: 'path', value: '/profile', label: 'Profile' },
  { type: 'path', value: '/payment-methods', label: 'Payment methods' },
  { type: 'path', value: '/invoices', label: 'Invoices' },
  { type: 'path', value: '/invoice-detail', label: 'An invoice' },
  { type: 'path', value: '/members', label: 'Members', note: 'business accounts only' },
  { type: 'path', value: '/webview', label: 'Support, privacy or terms' },
  { type: 'path', value: '/splash', label: 'Launch screen', note: 'only when the app starts from cold' },
];

/** What the tag reports by itself, from the host pages. */
const TAG_EVENTS: StepChoice[] = [
  { type: 'event', value: 'form_start', label: 'Started filling in a form', note: 'your own pages’ forms' },
  { type: 'event', value: 'form_submit', label: 'Sent a form', note: 'your own pages’ forms' },
  { type: 'event', value: 'contact_click', label: 'Pressed a phone, email or WhatsApp link' },
];

const WIDGET_CHOICES: StepChoice[] = [
  ANY_PAGE,
  { ...QUOTED, note: 'in the reservation widget' },
  { ...BOOKED, note: 'in the reservation widget, or by your own confirmation line' },
  ...TAG_EVENTS,
];

const WEB_CHOICES: StepChoice[] = [
  ANY_PAGE,
  { ...BOOKED, note: 'reported by the line in step 2 of Install' },
  ...TAG_EVENTS,
];

const KNOWN: Record<InstallKind, StepChoice[]> = { app: APP_CHOICES, widgets: WIDGET_CHOICES, web: WEB_CHOICES };

export interface StepChoices {
  /** What we know this kind of install reports. */
  known: StepChoice[];
  /** Pages this install has been seen reporting that the known list does not name. */
  seen: StepChoice[];
}

const SEEN_MAX = 25;

/**
 * The known list, plus the pages this install has actually reported that it
 * does not already name — which is all a website's own pages will ever be, and
 * on the app is how a screen added after this list was written still shows up.
 * `seen` is busiest first, as the top-pages read answers it.
 */
export const choicesFor = (kind: InstallKind, seenPaths: readonly string[] = []): StepChoices => {
  const reported = new Set(seenPaths);
  const known = KNOWN[kind].filter((choice) => !choice.onceSeen || reported.has(choice.value));
  const named = new Set(known.filter((choice) => choice.type === 'path').map((choice) => choice.value));
  const seen: StepChoice[] = [];
  for (const raw of seenPaths) {
    // The rollup writes '(unknown)' for a pageview with no path; it is not a page.
    const path = String(raw || '').trim();
    if (!path.startsWith('/') || named.has(path)) continue;
    named.add(path);
    seen.push({ type: 'path', value: path, label: path });
    if (seen.length >= SEEN_MAX) break;
  }
  return { known, seen };
};

/** The choice a step points at, or null when it is something typed by hand (or nothing yet). */
export const choiceOf = (step: Pick<FunnelStep, 'match'>, choices: StepChoices): StepChoice | null => {
  const all = [...choices.known, ...choices.seen];
  const type = step.match?.type ?? 'path';
  const value = step.match?.value ?? '';
  if (!value) return null;
  return all.find((c) => c.type === type && c.value === value) ?? null;
};

/* ---- ready-made funnels ------------------------------------------------- */

export interface FunnelTemplate {
  key: string;
  title: string;
  steps: FunnelStep[];
  /** The step a lead is raised from, by key — or none. */
  leadStep: string | null;
}

const step = (key: string, choice: StepChoice, label = choice.label): FunnelStep => ({
  key,
  label,
  match: { type: choice.type, value: choice.value },
});

/**
 * The page a website's visitors book on, from what it has reported: the busiest
 * path that reads like one. Only a guess, so only ever a template's starting
 * point — and none at all rather than `/`, which would make the step "any page"
 * twice over.
 */
export const bookingPageOf = (seenPaths: readonly string[]): string | null =>
  seenPaths.find((path) => path !== '/' && /book|reserv|quote/i.test(path)) ?? null;

/**
 * Why these and not one funnel with everything in it: the funnel read counts
 * each step on its own, so a quote step between "opened the form" and "booked"
 * would show everyone who booked without a quote as lost at the quote. A booking
 * and a quote are two journeys, so they are two funnels.
 */
export const templatesFor = (kind: InstallKind, seenPaths: readonly string[] = []): FunnelTemplate[] => {
  if (kind === 'app') {
    const form = APP_CHOICES.find((choice) => choice.value === '/book-now') as StepChoice;
    const fleet = APP_CHOICES.find((choice) => choice.value === '/fleet') as StepChoice;
    const vehicle = APP_CHOICES.find((choice) => choice.value === '/fleet-details') as StepChoice;
    const [trip, car, details, summary, payment] = BOOKING_STEPS;
    const stepByStep: FunnelTemplate[] = reportsBookingSteps(seenPaths)
      ? [
          {
            key: 'booking_steps',
            title: 'Reservation, step by step',
            steps: [
              step('opened', ANY_SCREEN),
              step('trip', trip, 'Entered the trip'),
              step('vehicle', car, 'Chose a vehicle'),
              step('details', details, 'Gave their details'),
              step('summary', summary, 'Reached the summary'),
              step('payment', payment, 'Reached payment'),
              step('booked', BOOKED),
            ],
            leadStep: 'details',
          },
          {
            key: 'quote_steps',
            title: 'Quote, step by step',
            steps: [
              step('opened', ANY_SCREEN),
              step('trip', trip, 'Entered the trip'),
              step('vehicle', car, 'Chose a vehicle'),
              step('details', details, 'Gave their details'),
              step('quoted', QUOTED),
            ],
            leadStep: 'details',
          },
        ]
      : [];
    return [
      ...stepByStep,
      {
        key: 'booking',
        title: 'Reservation',
        steps: [step('opened', ANY_SCREEN), step('form', form, 'Opened the reservation form'), step('booked', BOOKED)],
        leadStep: 'form',
      },
      {
        key: 'quote',
        title: 'Quote',
        steps: [step('opened', ANY_SCREEN), step('form', form, 'Opened the reservation form'), step('quoted', QUOTED)],
        leadStep: 'form',
      },
      {
        key: 'fleet',
        title: 'From the fleet',
        steps: [
          step('opened', ANY_SCREEN),
          step('fleet', fleet, 'Browsed the fleet'),
          step('vehicle', vehicle, 'Looked at a vehicle'),
          step('form', form, 'Opened the reservation form'),
          step('booked', BOOKED),
        ],
        leadStep: 'form',
      },
    ];
  }

  const page = bookingPageOf(seenPaths);
  const landed = step('landed', ANY_PAGE);
  const bookingPage: FunnelStep[] = page
    ? [{ key: 'booking_page', label: 'Opened the reservation page', match: { type: 'path', value: page } }]
    : [];
  const lead = page ? 'booking_page' : null;
  const booking: FunnelTemplate = {
    key: 'booking',
    title: 'Reservation',
    steps: [landed, ...bookingPage, step('booked', BOOKED)],
    leadStep: lead,
  };
  if (kind === 'web') return [booking];
  return [
    booking,
    { key: 'quote', title: 'Quote', steps: [landed, ...bookingPage, step('quoted', QUOTED)], leadStep: lead },
  ];
};

/** "Opened the app → Opened the booking form → Booked", for a button's title. */
export const templateSummary = (template: FunnelTemplate): string =>
  template.steps.map((entry) => entry.label).join(' → ');
